import React, { useState } from 'react';
import { Utensils, Binoculars, ShoppingBag, Mountain, Star, MapPin, ExternalLink } from 'lucide-react';

const categories = [
  { id: 'sightseeing', name: 'Sightseeing', icon: Binoculars },
  { id: 'food-nearby', name: 'Food (Nearby)', icon: Utensils },
  { id: 'food-drive', name: 'Food (1 Hour)', icon: Utensils },
  { id: 'grocery', name: 'Grocery & Shops', icon: ShoppingBag },
  { id: 'hiking', name: 'Hiking', icon: Mountain },
];

const guidebookData = {
  'sightseeing': [
    { name: 'Schoodic Point', note: '148 locals recommend', description: 'Dramatic granite shoreline within Acadia National Park — the less-crowded gem of Downeast Maine.' },
    { name: 'Schoodic Beach', note: '6 locals recommend', description: 'Secluded sandy beach on the Schoodic Peninsula, perfect for a peaceful day by the water.' },
    { name: 'Schoodic Head', note: 'Panoramic views', description: 'Hike to the summit for breathtaking 360-degree views of the coast and islands.' },
    { name: 'Maine Coastal Islands Wildlife Refuge', note: '17 locals recommend', description: 'Pristine island habitats for seabirds and seals along the Maine coast.' },
    { name: '"Catch Your Dinner" Lobster Boat Tours', note: '17 locals recommend', description: 'Haul lobster traps with a local fisherman — a quintessential Maine experience.' },
    { name: 'Corea Heath Wildlife Refuge', note: '6 locals recommend', description: 'Unique coastal heath with boardwalk trails through wildflower meadows.' },
    { name: 'Big Jim The Fisherman Statue', note: 'Local landmark', description: 'Iconic roadside statue celebrating Maine\'s fishing heritage in Prospect Harbor.' },
    { name: 'Tidal Falls Preserve', note: '47 locals recommend', description: 'Watch reversing tidal falls, spot seals, and enjoy a perfect picnic spot.' },
  ],
  'food-nearby': [
    { name: 'The Pickled Wrinkle', note: '117 locals recommend', description: 'Only 3 minutes from the house! Great food and pizza right on the harbor.' },
    { name: 'J.M. Gerrish Cafe', note: '46 locals recommend', description: 'Great breakfast spot — only 10 minutes away in Winter Harbor.' },
    { name: 'Musquito Harbor Brewing Company', note: '21 locals recommend', description: 'Local craft brewery just 3 minutes away! Enjoy fresh brews with ocean views.' },
    { name: 'Me And Ben\'s Dairy Creme', note: '34 locals recommend', description: 'Classic Maine ice cream and dairy treats — a must-stop on summer days.' },
    { name: 'Bunker\'s Seafood and Spirits', note: '37 locals recommend', description: 'Fresh local seafood and cocktails in a casual coastal atmosphere.' },
    { name: 'Treehouse Seaside Grill', note: '21 locals recommend', description: 'Casual seaside dining with great views and local favorites.' },
    { name: 'Dinner', note: 'Fine dining', description: 'Upscale dining experience featuring local ingredients and creative cuisine.' },
  ],
  'food-drive': [
    { name: 'YU Take Out', note: 'Asian cuisine', description: 'Delicious Asian takeout with generous portions and bold flavors.' },
    { name: 'The Ovens Restaurant', note: 'Waterfront dining', description: 'Stunning waterfront restaurant with creative New England cuisine.' },
    { name: 'Brick House Soups', note: 'Comfort food', description: 'Hearty homemade soups and sandwiches — perfect for a rainy Maine day.' },
    { name: 'Helen\'s Restaurant of Ellsworth', note: '53 locals recommend', description: 'Legendary local institution famous for massive portions and pies.' },
    { name: '86 This', note: '89 locals recommend', description: 'Trendy gastropub with creative cocktails and innovative small plates.' },
    { name: 'ThaiConic', note: 'Thai cuisine', description: 'Authentic Thai dishes bringing Southeast Asian flavors to Downeast Maine.' },
    { name: 'Taste Jamaica', note: '17 locals recommend', description: 'Caribbean flavors in Maine — jerk chicken, curry goat, and island vibes.' },
    { name: 'Union River Lobster Pot', note: '126 locals recommend', description: 'The lobster destination of Ellsworth — fresh catches cooked to perfection.' },
    { name: 'Zeppa\'s Pizza', note: '25 locals recommend', description: 'Wood-fired pizza and Italian favorites loved by locals.' },
  ],
  'grocery': [
    { name: 'MC\'s Marketplace', note: '13 locals recommend', description: 'Local market with fresh produce, deli, and essentials close to the property.' },
    { name: 'Winter Harbor Provisions', note: '27 locals recommend', description: 'Well-stocked general store with local goods, groceries, and harbor charm.' },
    { name: 'Dollar General', note: '10 locals recommend', description: 'Convenient store for everyday essentials and household items.' },
  ],
  'hiking': [
    { name: 'Schoodic Head', note: 'Moderate hike', description: 'The highest point on Schoodic Peninsula with panoramic ocean and island views.' },
    { name: 'Cadillac Mountain', note: '54 locals recommend', description: 'The first place in the US to see sunrise — an iconic Acadia experience atop the tallest peak on the Atlantic coast.' },
  ],
};

const GuidebookSection = () => {
  const [activeCategory, setActiveCategory] = useState('sightseeing');

  const items = guidebookData[activeCategory] || [];

  return (
    <section id="guidebook" className="section-spacing" data-testid="guidebook-section" style={{ background: 'var(--color-coastal-fog)' }}>
      <div className="container-coastal">
        <div className="text-center mb-xl">
          <h2 className="section-title">Discover Schoodic Peninsula</h2>
          <p className="section-subtitle" style={{ maxWidth: '700px', margin: '0 auto' }}>
            Your local guidebook for the best food, attractions, and hidden gems — curated by your host Donald
          </p>
        </div>

        {/* Category Tabs */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', justifyContent: 'center',
          gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-2xl)'
        }}>
          {categories.map((cat) => {
            const Icon = cat.icon;
            const isActive = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                data-testid={`guidebook-tab-${cat.id}`}
                onClick={() => setActiveCategory(cat.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.75rem 1.25rem',
                  background: isActive ? 'var(--color-ocean-deep)' : 'white',
                  color: isActive ? 'white' : 'var(--color-granite-gray)',
                  border: `2px solid ${isActive ? 'var(--color-ocean-deep)' : 'var(--color-coastal-fog)'}`,
                  borderRadius: 'var(--border-radius-lg)',
                  cursor: 'pointer', fontWeight: '600', fontSize: '0.95rem',
                  transition: 'all 0.3s ease',
                  boxShadow: isActive ? '0 4px 15px rgba(10, 61, 98, 0.3)' : 'none'
                }}
              >
                <Icon size={18} />
                {cat.name}
              </button>
            );
          })}
        </div>

        {/* Guidebook Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: 'var(--spacing-lg)'
        }}>
          {items.map((item, i) => (
            <div key={i} data-testid={`guidebook-item-${i}`} style={{
              background: 'white',
              borderRadius: 'var(--border-radius-lg)',
              padding: 'var(--spacing-lg)',
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              borderLeft: '4px solid var(--color-ocean-deep)'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)'; }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                <h4 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', color: 'var(--color-dark-slate)', fontWeight: '700' }}>
                  {item.name}
                </h4>
              </div>
              {item.note && (
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                  padding: '0.25rem 0.75rem', marginBottom: '0.75rem',
                  background: 'var(--color-coastal-fog)', borderRadius: '20px',
                  fontSize: '0.8rem', color: 'var(--color-ocean-deep)', fontWeight: '600'
                }}>
                  <Star size={12} fill="var(--color-sunset-gold)" color="var(--color-sunset-gold)" />
                  {item.note}
                </div>
              )}
              <p style={{ fontSize: '0.9rem', color: 'var(--color-granite-gray)', lineHeight: 1.6 }}>
                {item.description}
              </p>
            </div>
          ))}
        </div>

        {/* Airbnb Guidebook Link */}
        <div className="text-center" style={{ marginTop: 'var(--spacing-2xl)' }}>
          <a
            href="https://www.airbnb.com/s/guidebooks?unique_share_id=05147d68-293a-4ada-a37f-59267f700882&refinement_paths%5B%5D=/guidebooks/6543400"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="view-full-guidebook-link"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
              padding: '1rem 2rem', background: 'var(--color-ocean-deep)',
              color: 'white', borderRadius: 'var(--border-radius-lg)',
              textDecoration: 'none', fontWeight: '600', fontSize: '1rem',
              transition: 'background 0.3s ease'
            }}
          >
            View Full Guidebook on Airbnb
            <ExternalLink size={18} />
          </a>
        </div>
      </div>
    </section>
  );
};

export default GuidebookSection;
